import { fetchAuthSession, signOut } from 'aws-amplify/auth'
import { awsConfig } from '../config/aws'
import type { ConnectionType, ContactStatus, OutreachStage } from '../constants/networking'
import type { PortalContentAvailability } from '../constants/stageVisibility'

const API_ENDPOINT = awsConfig.API.REST.missfit.endpoint

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
}

export class ApiError extends Error {
  status: number
  field?: string
  /**
   * The error body as the server sent it. The thrown message is the server's
   * error code whenever it sends one, because that is what callers branch on;
   * anything else the body carries — a count, a list — is read from here.
   */
  details?: Record<string, unknown>
  constructor(
    message: string,
    status: number,
    field?: string,
    details?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.field = field
    this.details = details
  }
}

async function getAuthToken(): Promise<string> {
  try {
    // forceRefresh: true will attempt to refresh if token is expired
    const session = await fetchAuthSession({ forceRefresh: true })
    const token = session.tokens?.idToken?.toString()
    if (!token) {
      throw new Error('No valid token')
    }
    return token
  } catch (error) {
    // If we can't get a token, sign out and redirect to login
    console.error('Failed to get auth token:', error)
    await signOut()
    redirectToLoginWithNotice()
    throw error
  }
}

// MWA-286 — the session-expired copy used to be thrown straight after the
// redirect was initiated, so the document unloaded before any catch handler
// could paint it and /login had no idea why the user had arrived. Hand the
// reason over in sessionStorage instead, which survives the navigation, and
// let Login render it.
export const SESSION_NOTICE_KEY = 'authSessionNotice'

function redirectToLoginWithNotice() {
  try {
    sessionStorage.setItem(SESSION_NOTICE_KEY, 'Your session has ended. Sign in to continue.')
  } catch {
    // Private-mode / storage-disabled browsers still get the redirect, just
    // without the explanation. Never block sign-out on a storage failure.
  }
  window.location.href = '/login'
}

export async function apiCall<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body } = options
  const token = await getAuthToken()

  const response = await fetch(`${API_ENDPOINT}/${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  // Handle 401 Unauthorized - token may have expired
  if (response.status === 401) {
    console.error('Unauthorized response - signing out')
    await signOut()
    redirectToLoginWithNotice()
    throw new ApiError('Your session has ended. Sign in to continue.', 401)
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Something went wrong. Try refreshing the page.' }))
    throw new ApiError(
      error.error || error.message || `API error: ${response.status}`,
      response.status,
      error.field,
      error
    )
  }

  const json = await response.json()
  // Backend returns { message, data } - extract data
  return (json.data !== undefined ? json.data : json) as T
}

// Public API call without authentication (for set-password, etc.)
export async function publicApiCall<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body } = options

  const response = await fetch(`${API_ENDPOINT}/${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Something went wrong. Try refreshing the page.' }))
    throw new ApiError(
      error.error || error.message || `API error: ${response.status}`,
      response.status,
      error.field,
      error
    )
  }

  const json = await response.json()
  return (json.data !== undefined ? json.data : json) as T
}

// Product types
export interface Product {
  id: string
  name: string
  description?: string
  type: 'resume' | 'linkedin' | 'coaching' | 'workshop' | 'interview_prep' | 'other'
  price: number
  status: 'active' | 'inactive'
  sortOrder: number
  features: string[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface CreateProductInput {
  name: string
  description?: string
  type: Product['type']
  price?: number
  status?: Product['status']
  sortOrder?: number
  features?: string[]
}

export interface UpdateProductInput extends Partial<CreateProductInput> {
  id: string
}

// Package types
export interface Package {
  id: string
  name: string
  description?: string
  productIds: string[]
  price: number
  features?: string[]
  status: 'active' | 'inactive'
  sortOrder: number
  isPopular: boolean
  discount?: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface CreatePackageInput {
  name: string
  description?: string
  productIds?: string[]
  price?: number
  features?: string[]
  status?: Package['status']
  sortOrder?: number
  isPopular?: boolean
  discount?: string
}

export interface UpdatePackageInput extends Partial<CreatePackageInput> {
  id: string
}

// Lead types
export interface Lead {
  id: string
  name: string
  email: string
  phone?: string
  source: string
  status: 'new' | 'contacted' | 'qualified' | 'not_qualified' | 'won' | 'lost'
  salaryTarget?: string
  notes?: string
  assignedTo?: string
  // Product/package selection
  selectedProducts?: string[]
  selectedPackageId?: string
  createdAt: string
  updatedAt: string
  statusHistory?: Array<{ status: string; timestamp: string; changedBy?: string }>
}

export interface CreateLeadInput {
  name: string
  email: string
  phone?: string
  source: string
  salaryTarget?: string
  notes?: string
  selectedProducts?: string[]
  selectedPackageId?: string
}

export interface UpdateLeadInput {
  id: string
  name?: string
  email?: string
  phone?: string
  source?: string
  status?: Lead['status']
  salaryTarget?: string
  notes?: string
  assignedTo?: string
  selectedProducts?: string[]
  selectedPackageId?: string
}

// Lead API functions
export const leadsApi = {
  list: async (options?: { all?: boolean }): Promise<Lead[]> => {
    const params = options?.all ? '?all=true' : ''
    return apiCall<Lead[]>(`list-leads${params}`)
  },

  create: async (data: CreateLeadInput): Promise<Lead> => {
    return apiCall<Lead>('leads', { method: 'POST', body: data })
  },

  update: async (data: UpdateLeadInput): Promise<Lead> => {
    return apiCall<Lead>('update-lead', { method: 'POST', body: data })
  },

  qualify: async (id: string, qualified: boolean, reason?: string): Promise<Lead> => {
    return apiCall<Lead>('qualify-lead', {
      method: 'POST',
      body: { id, qualified, reason },
    })
  },

  convertToClient: async (
    id: string,
    teamData?: { clientSuccess?: string; resumeWriter?: string; coach?: string }
  ): Promise<{ client: Client; lead: Lead }> => {
    return apiCall<{ client: Client; lead: Lead }>('convert-lead', {
      method: 'POST',
      body: { id, ...teamData },
    })
  },

  generateProposal: async (
    leadId: string,
    options?: { servicePackage?: string; consultation?: Record<string, unknown> }
  ): Promise<{ proposal: string; leadInfo: Record<string, unknown> }> => {
    return apiCall('generate-proposal', {
      method: 'POST',
      body: { leadId, ...options },
    })
  },

  sendProposalEmail: async (
    leadId: string,
    options?: { subject?: string }
  ): Promise<{ leadId: string; sentTo: string; sentAt: string; messageId: string; status: string }> => {
    return apiCall('send-proposal-email', {
      method: 'POST',
      body: { leadId, ...options },
    })
  },
}

// Client Intake types
export interface Education {
  degree: string
  institution: string
  year?: string
  field?: string
}

export interface Experience {
  title: string
  company: string
  startDate?: string
  endDate?: string
  description?: string
  achievements?: string[]
}

export interface ClientIntake {
  currentRole?: string
  currentCompany?: string
  yearsExperience?: number
  education: Education[]
  certifications: string[]
  experience: Experience[]
  skills: string[]
  tools: string[]
  careerGoals?: string
  motivations?: string
  challenges?: string
  linkedInUrl?: string
  portfolioUrl?: string
  additionalNotes?: string
  completedAt?: string
  completedBy?: string
}

export interface CliftonStrength {
  id: string
  name: string
  domain: 'executing' | 'influencing' | 'relationship_building' | 'strategic_thinking'
  rank: number
  description?: string
  createdAt?: string
  createdBy?: string
  updatedAt?: string
  updatedBy?: string
}

// The tiers staff can pick and the only ones stored on a company. 'unlisted'
// (a company not on the list at all) exists only on a derived companyTier.
// An older record can still hold the retired 'goal' until the migration runs;
// utils/companyTier reads it as 'target'.
export type TargetCompanyTier = 'dream' | 'target' | 'backup' | 'wildcard'

export interface TargetCompany {
  name: string
  tier: TargetCompanyTier
  // Folded in from the retired standalone target-companies table (MWA-254
  // consolidation) — all optional, since a company typed by hand into this
  // Dream Job form carries none of them, while one added via the Job Search
  // "Target Companies" tab or seeded from the North Star can carry all of
  // them. Reuses EmployerQuality/TargetCompanyCategory (declared further down
  // with the old table's types) since the values are identical.
  employerQuality?: EmployerQuality
  // WHY this company is on the list — a different question from `tier` (HOW
  // MUCH the client wants it). Deliberately shares the 'wildcard' value with
  // tier: it's the client's own workbook term. Label the two separately in
  // the UI so a reader never conflates them.
  listReason?: TargetCompanyCategory
  location?: string
  rationale?: string
  source?: string
}

export interface DreamJob {
  targetRole?: string
  targetIndustry?: string
  targetCompanies: TargetCompany[]
  targetJobLinks?: string[]
  salaryTarget?: string
  salaryMinimum?: string
  benefitsPriorities: string[]
  workStyle?: 'remote' | 'hybrid' | 'onsite'
  locationPreferences: string[]
  willingToRelocate: boolean
  priorities: string[]
  dealBreakers: string[]
  targetStartDate?: string
  urgency: 'urgent' | 'moderate' | 'flexible'
  additionalRequirements?: string
  completedAt?: string
  completedBy?: string
}

// Comment types
export interface Comment {
  id: string
  content: string
  parentId: string | null
  mentions: string[]
  createdAt: string
  createdBy: string
  editedAt: string | null
}

// Activity log types
export interface ActivityLogEntry {
  id: string
  entityId: string
  entityType: string
  eventType: string
  userId: string
  userDisplayName: string
  userEmail: string
  resultingState: string
  createdAt: string
}

// Client types
export interface Client {
  id: string
  leadId?: string
  name: string
  email: string
  phone?: string
  workflowStage:
    | 'onboarding'
    | 'pre_north_star'
    | 'north_star'
    | 'strategy_session'
    | 'deliverables_in_progress'
    | 'deliverables_complete'
    | 'linkedin_workshop'
    | 'market_ready'
    | 'complete'
  status?: string
  resumeStatus?: 'not_started' | 'in_progress' | 'in_review' | 'complete'
  linkedinStatus?: 'not_started' | 'in_progress' | 'complete' | 'not_needed'
  assignedTo?: string
  // Team assignments
  clientSuccess?: string
  resumeWriter?: string
  coach?: string
  teamAssignedAt?: string
  teamAssignedBy?: string
  // Service agreement approval (pre-onboarding gate)
  serviceAgreementApproved?: boolean
  serviceAgreementApprovedAt?: string
  serviceAgreementApprovedBy?: string
  // MWA-308 — gates the Search Firm surface. Absent reads as 'standard'; see
  // utils/clientTier.ts, which owns that default.
  clientTier?: 'executive' | 'standard'
  // MWA-357 — gates the Wild Card Outreach surface, independent of the tier.
  // Absent reads as off; see utils/clientTier.ts.
  wildcardOutreachEnabled?: boolean
  // Intake data
  intake?: ClientIntake
  intakeCompletedAt?: string
  intakeCompletedBy?: string
  cliftonStrengths?: CliftonStrength[]
  cliftonStrengthsCompletedAt?: string
  cliftonStrengthsCompletedBy?: string
  dreamJob?: DreamJob
  dreamJobCompletedAt?: string
  dreamJobCompletedBy?: string
  // Resume data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resumeContent?: string | Record<string, any>
  resumeGeneratedAt?: string
  resumeGeneratedBy?: string
  resumeGenerationStatus?: 'generating' | 'complete' | 'failed'
  resumeIsDraft?: boolean
  resumePublishMode?: 'draft' | 'ready'
  // Which resume the client has: the one the AI generates, or a file staff upload.
  // Absent means 'ai', as it did for every client before sources existed.
  resumeSource?: 'ai' | 'uploaded'
  // The designated file of an uploaded resume (a resume_document attachment).
  resumeAttachmentId?: string
  // Resume approval
  resumeApproved?: boolean
  resumeApprovedAt?: string
  // Internal resume approval (staff)
  internalResumeApproved?: boolean
  internalApprovedBy?: string
  internalApprovedByRole?: string
  internalApprovedAt?: string
  // North Star approval
  northStarApproved?: boolean
  northStarApprovedAt?: string
  // Internal North Star approval (staff)
  northStarInternalApproved?: boolean
  northStarInternalApprovedBy?: string
  northStarInternalApprovedByRole?: string
  northStarInternalApprovedAt?: string
  // Six lambdas append to this array in two different shapes, and only
  // timestamp and changedBy are common to all of them. A stage CHANGE
  // (advance-workflow-stage, interview-offer-accepted) writes
  // fromStage/toStage/action/notes; a stage-form COMPLETION
  // (save-client-intake, save-dream-job, save-clifton-strengths, update-client)
  // writes stage, sometimes with action. Everything shape-specific is therefore
  // optional — asserting either shape would be false for the other four
  // writers. Nothing reads this array yet; when something does, it has to
  // handle both.
  stageHistory?: Array<{
    timestamp: string
    changedBy: string
    fromStage?: string | null
    toStage?: string
    stage?: string
    action?: string
    notes?: string | null
  }>
  comments?: Comment[]
  progress?: number
  incompleteTaskCount?: number
  networkingContactCount?: number
  hasLinkedinImport?: boolean
  hasZeroTargetMatches?: boolean
  // Per-client unread counters (inbound messages awaiting team response).
  // Incremented by email-webhook / sms-webhook on inbound, decremented by
  // mark-email-read and zeroed in bulk by the ws-default `mark_client_read`
  // action when a team member opens the client's comms thread.
  unreadEmailCount?: number
  unreadSmsCount?: number
  createdAt: string
  updatedAt: string
}

// Team assignment input
export interface AssignClientTeamInput {
  clientId: string
  clientSuccess?: string
  resumeWriter?: string
  coach?: string
}

// Client API functions
export const clientsApi = {
  list: async (options?: { all?: boolean; needsTeamAssignment?: boolean }): Promise<Client[]> => {
    const params = new URLSearchParams()
    if (options?.all) params.append('all', 'true')
    if (options?.needsTeamAssignment) params.append('needsTeamAssignment', 'true')
    const queryString = params.toString()
    return apiCall<Client[]>(`list-clients${queryString ? `?${queryString}` : ''}`)
  },

  get: async (id: string): Promise<Client> => {
    return apiCall<Client>(`get-client?id=${id}`)
  },

  create: async (data: Partial<Client>): Promise<Client> => {
    return apiCall<Client>('clients', { method: 'POST', body: data })
  },

  update: async (data: Partial<Client> & { id: string }): Promise<Client> => {
    return apiCall<Client>('update-client', { method: 'POST', body: data })
  },

  delete: async (id: string): Promise<void> => {
    return apiCall<void>('delete-client', { method: 'POST', body: { id } })
  },

  assignTeam: async (data: AssignClientTeamInput): Promise<Client> => {
    return apiCall<Client>('assign-client-team', { method: 'POST', body: data })
  },

  getNeedingTeamAssignment: async (): Promise<Client[]> => {
    return apiCall<Client[]>('list-clients?all=true&needsTeamAssignment=true')
  },

  // Intake operations
  saveIntake: async (
    clientId: string,
    intake: Omit<ClientIntake, 'completedAt' | 'completedBy'>
  ): Promise<{ clientId: string; intake: ClientIntake }> => {
    return apiCall('client-intake', {
      method: 'POST',
      body: { clientId, ...intake },
    })
  },

  parseResumeIntake: async (
    attachmentId: string
  ): Promise<{
    intake: Omit<ClientIntake, 'completedAt' | 'completedBy'>
    sourceAttachment: { id: string; fileName: string; contentType: string }
  }> => {
    return apiCall('parse-resume-intake', {
      method: 'POST',
      body: { attachmentId },
    })
  },

  // Clifton Strengths operations
  saveCliftonStrengths: async (
    clientId: string,
    strengths: Omit<CliftonStrength, 'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>[]
  ): Promise<{ clientId: string; cliftonStrengths: CliftonStrength[]; count: number; isComplete: boolean }> => {
    return apiCall('clifton-strengths', {
      method: 'POST',
      body: { clientId, strengths },
    })
  },

  addCliftonStrength: async (
    clientId: string,
    strength: Omit<CliftonStrength, 'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>
  ): Promise<{ clientId: string; cliftonStrengths: CliftonStrength[]; count: number; isComplete: boolean }> => {
    return apiCall('clifton-strengths', {
      method: 'POST',
      body: { clientId, operation: 'add', strength },
    })
  },

  updateCliftonStrength: async (
    clientId: string,
    strengthId: string,
    strength: Partial<Omit<CliftonStrength, 'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>>
  ): Promise<{ clientId: string; cliftonStrengths: CliftonStrength[]; count: number; isComplete: boolean }> => {
    return apiCall('clifton-strengths', {
      method: 'POST',
      body: { clientId, operation: 'update', strengthId, strength },
    })
  },

  deleteCliftonStrength: async (
    clientId: string,
    strengthId: string
  ): Promise<{ clientId: string; cliftonStrengths: CliftonStrength[]; count: number; isComplete: boolean }> => {
    return apiCall('clifton-strengths', {
      method: 'POST',
      body: { clientId, operation: 'delete', strengthId },
    })
  },

  // Dream Job operations
  saveDreamJob: async (
    clientId: string,
    dreamJob: Omit<DreamJob, 'completedAt' | 'completedBy'>
  ): Promise<{ clientId: string; dreamJob: DreamJob }> => {
    return apiCall('dream-job', {
      method: 'POST',
      body: { clientId, ...dreamJob },
    })
  },

  // Workflow stage operations
  advanceWorkflowStage: async (
    clientId: string,
    targetStage: Client['workflowStage'],
    options?: { notes?: string; override?: boolean; overrideReason?: string }
  ): Promise<{
    clientId: string
    previousStage: string
    currentStage: string
    stageLabel: string
    overrideUsed?: boolean
  }> => {
    return apiCall('advance-workflow', {
      method: 'POST',
      body: { clientId, targetStage, ...options },
    })
  },
}

// Task types
export interface Task {
  id: string
  title: string
  description?: string
  clientId?: string
  clientName?: string
  type: 'intake' | 'resume' | 'linkedin' | 'follow_up' | 'feedback' | 'gate'
  priority: 'low' | 'medium' | 'high'
  status: 'pending' | 'in_progress' | 'completed'
  dueDate?: string
  assignedTo?: string
  createdBy?: string
  createdAt: string
  updatedAt: string
  // Gate task fields
  isGateTask?: boolean
  stageId?: string
  gateTaskTemplateId?: string
  required?: boolean
  sortOrder?: number
  completedBy?: string
  completedAt?: string
  notes?: string
  isClientFacing?: boolean
  isDeleted?: boolean
}

// Task API functions
export const tasksApi = {
  list: async (options?: {
    all?: boolean
    clientId?: string
    status?: string
    type?: string
    stageId?: string
    isGateTask?: boolean
    excludeGateTasks?: boolean
  }): Promise<Task[]> => {
    const params = new URLSearchParams()
    if (options?.all) params.append('all', 'true')
    if (options?.clientId) params.append('clientId', options.clientId)
    if (options?.status) params.append('status', options.status)
    if (options?.type) params.append('type', options.type)
    if (options?.stageId) params.append('stageId', options.stageId)
    if (options?.isGateTask !== undefined) params.append('isGateTask', String(options.isGateTask))
    if (options?.excludeGateTasks) params.append('excludeGateTasks', 'true')
    const queryString = params.toString()
    return apiCall<Task[]>(`tasks${queryString ? `?${queryString}` : ''}`)
  },

  create: async (data: Partial<Task>): Promise<Task> => {
    return apiCall<Task>('new-task', { method: 'POST', body: data })
  },

  update: async (data: Partial<Task> & { id: string }): Promise<Task> => {
    return apiCall<Task>('update-task', { method: 'POST', body: data })
  },

  complete: async (id: string): Promise<Task> => {
    return apiCall<Task>('complete-task', { method: 'POST', body: { id } })
  },

  delete: async (id: string): Promise<{ id: string }> => {
    return apiCall<{ id: string }>('delete-task', { method: 'POST', body: { id } })
  },
}

// Dashboard types
export interface DashboardData {
  activeLeads: number
  activeClients: number
  // Applications that entered Applied in the last 7 days, and the number of
  // distinct clients with at least one application at Interviewing. Both are
  // derived server-side from the same jobs read as pendingApplications.
  applicationsThisWeek: number
  clientsInterviewing: number
  leadsByStatus: Record<string, number>
  recentTasks: Task[]
  pendingApplications?: {
    id: string
    clientId: string
    clientName: string
    company: string
    jobTitle: string
    applyDecision: 'apply_on_behalf' | 'self_apply'
    // MWA-361 — optional so an older deploy without it still renders.
    url?: string
  }[]
  // MWA-341 — the three "needs you" counts, all derived server-side from the
  // same reads the tiles above already use. Optional so a client running
  // against an older deploy renders the rest of the dashboard rather than
  // NaN tiles.
  awaitingClientDecision?: number
  stalledApplications?: number
  overdueTasks?: number
  // How many days of silence makes an application stalled. Comes from the
  // server so this label and the board's inactivity chips cannot quote
  // different numbers.
  staleThresholdDays?: number
  // Clients per workflow stage, keyed by stage ID. Labels are Stage Gates
  // configuration, so the caller resolves them from
  // workflowStageDefinitionsAtom rather than trusting a code constant.
  clientsByStage?: Record<string, number>
}

// Dashboard API
export const dashboardApi = {
  getData: async (): Promise<DashboardData> => {
    return apiCall<DashboardData>('dashboard')
  },
}

// User types
export type UserRole = 'admin' | 'sales' | 'client_success' | 'resume_writer' | 'coach' | 'client'
export type UserStatus = 'active' | 'inactive' | 'suspended' | 'deleted'

export interface User {
  email: string
  name: string
  role: UserRole
  status: UserStatus
  phone?: string
  emailSignature?: string
  isManager?: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateUserInput {
  email: string
  name: string
  role: UserRole
  phone?: string
  isManager?: boolean
}

export interface UpdateUserInput {
  email: string
  name?: string
  role?: UserRole
  status?: UserStatus
  phone?: string
  emailSignature?: string
  isManager?: boolean
}

// User API functions
export const usersApi = {
  list: async (options?: { role?: UserRole; status?: UserStatus; includeDeleted?: boolean }): Promise<User[]> => {
    const params = new URLSearchParams()
    if (options?.role) params.append('role', options.role)
    if (options?.status) params.append('status', options.status)
    if (options?.includeDeleted) params.append('includeDeleted', 'true')
    const queryString = params.toString()
    return apiCall<User[]>(`list-users${queryString ? `?${queryString}` : ''}`)
  },

  create: async (data: CreateUserInput): Promise<User> => {
    return apiCall<User>('add-user', { method: 'POST', body: data })
  },

  update: async (data: UpdateUserInput): Promise<User> => {
    return apiCall<User>('update-user', { method: 'POST', body: data })
  },

  delete: async (email: string): Promise<void> => {
    return apiCall<void>('delete-user', { method: 'POST', body: { email } })
  },
}

// Interview types
export type InterviewStatus =
  | 'interview_requested'
  | 'prep_guide_sent'
  | 'scheduling'
  | 'prepped'
  | 'offer_accepted'

// How a round ended, in MissFit's own words — 'silence' is an employer who
// never replied, which their process chases rather than treats as unknown.
// Mirrors INTERVIEW_ROUND_OUTCOMES in backend/layers/helpers/nodejs/interviewLinkage.mjs,
// which is what actually validates the write.
// Keep in step with INTERVIEW_ROUND_OUTCOMES in
// backend/layers/helpers/nodejs/interviewLinkage.mjs, which update-interview
// validates against. proceed_to_next_stage is the only one that is not an
// ending: the employer is moving the client on to another round.
export type InterviewRoundOutcome =
  | 'offer'
  | 'silence'
  | 'rejection'
  | 'proceed_to_next_stage'
  | 'counter_offer'

export interface InterviewRound {
  roundNumber: number
  type: string
  date?: string | null
  format?: string | null
  interviewerName?: string
  interviewerTitle?: string
  notes?: string
  debriefNotes?: string
  debriefCompletedAt?: string | null
  // Follow-through on the round. All three are absent on rounds created before
  // these fields existed — no migration was run — so every reader treats
  // undefined and null alike.
  thankYouSentAt?: string | null
  followUpSentAt?: string | null
  outcome?: InterviewRoundOutcome | null
  status: 'scheduled' | 'completed' | 'debriefed'
  createdAt: string
}

export interface MockInterviewFeedbackSummary {
  strengths: string[]
  weaknesses: string[]
  priorityFocusAreas: string[]
  clientFacingSummary: string
  coachFacingSummary: string
}

export interface MockInterviewHistoryEntry {
  sessionId: string
  date: string
  duration: number
  transcriptUrl?: string | null
  transcript?: { role: string; content: string; timestamp?: string }[]
  endedReason?: string
  endedSummary?: string | null
  feedbackStatus?: 'pending' | 'generating' | 'complete' | 'failed'
  feedbackSummary?: MockInterviewFeedbackSummary | null
  feedbackGeneratedAt?: string | null
  feedbackError?: string | null
  clientViewedFeedbackAt?: string | null
  coachReviewedFeedbackAt?: string | null
  // Per-session coach feedback (added by #199). The coach can optionally
  // author notes specifically for this mock interview entry, separate from
  // the AI-generated feedbackSummary and from the interview-level
  // coachReviewNotes (which is for debrief rounds, not mock sessions).
  // When coachFeedbackVisibleToClient is true the notes also render on the
  // client-side portal view; otherwise they're staff-only.
  coachFeedbackNotes?: string | null
  coachFeedbackAt?: string | null
  coachFeedbackBy?: string | null
  coachFeedbackVisibleToClient?: boolean
  createdAt?: string
}

export interface Interview {
  id: string
  clientId: string
  clientName: string
  company: string
  role: string
  status: InterviewStatus
  assignedTo: string
  interviewDate?: string
  notes?: string
  prepGuide?: string
  prepGuideGeneratedAt?: string
  prepGuideIsDraft?: boolean
  // 'generating' | 'complete' | 'failed' | 'not_available'. For a client the list
  // and get endpoints strip prepGuide on a draft and report 'not_available', which
  // is the only way the portal can tell "not written yet" from "written, not
  // released" — prepGuideIsDraft alone cannot, because the guide is already gone.
  prepGuideStatus?: string
  prepGuidePublishedAt?: string | null
  prepGuideViewedAt?: string | null
  rounds?: InterviewRound[]
  coachReviewStatus?: 'pending' | 'reviewed' | null
  coachReviewNotes?: string
  coachReviewedAt?: string
  coachReviewedBy?: string
  mockInterviewHistory?: MockInterviewHistoryEntry[]
  createdAt: string
  updatedAt: string
}

export interface CreateInterviewInput {
  clientId: string
  clientName: string
  company: string
  role: string
  interviewDate?: string
  assignedTo?: string
  notes?: string
}

export interface UpdateInterviewInput {
  id: string
  company?: string
  role?: string
  status?: InterviewStatus
  assignedTo?: string
  interviewDate?: string
  notes?: string
  prepGuide?: string
  coachReviewStatus?: string
  coachReviewNotes?: string
  addRound?: { type: string; date?: string; format?: string; interviewerName?: string; interviewerTitle?: string; notes?: string }
  updateRound?: { roundNumber: number; [key: string]: unknown }
  deleteRound?: { roundNumber: number }
  markPrepGuideViewed?: boolean
  markFeedbackViewed?: { sessionId: string; viewer: 'client' | 'coach' }
}

export interface StartMockInterviewResponse {
  sessionId: string
  ephemeralToken: string
  expiresAt: number
  model: string
}

export interface SaveMockInterviewInput {
  interviewId: string
  sessionId: string
  transcript: { role: string; content: string; timestamp?: string }[]
  durationSeconds: number
  endedReason: string
  endedSummary?: string | null
}

// Interview API functions
export const interviewsApi = {
  list: async (options?: { all?: boolean }): Promise<Interview[]> => {
    const params = options?.all ? '?all=true' : ''
    return apiCall<Interview[]>(`list-interviews${params}`)
  },

  get: async (id: string): Promise<Interview> => {
    return apiCall<Interview>(`get-interview?id=${id}`)
  },

  create: async (data: CreateInterviewInput): Promise<Interview> => {
    return apiCall<Interview>('new-interview', { method: 'POST', body: data })
  },

  update: async (data: UpdateInterviewInput): Promise<Interview> => {
    return apiCall<Interview>('update-interview', { method: 'POST', body: data })
  },

  markOfferAccepted: async (id: string): Promise<{ interview: Interview; client: Client }> => {
    return apiCall<{ interview: Interview; client: Client }>('interview-offer-accepted', {
      method: 'POST',
      body: { id },
    })
  },

  generateGuide: async (
    clientId: string,
    interviewId: string
  ): Promise<{
    status?: string
    guide?: unknown
    clientInfo?: Record<string, unknown>
    interview?: Record<string, unknown>
    interviewId?: string
  }> => {
    return apiCall('generate-interview-guide', {
      method: 'POST',
      body: { clientId, interviewId },
    })
  },

  startMockInterviewSession: async (
    interviewId: string
  ): Promise<StartMockInterviewResponse> => {
    return apiCall<StartMockInterviewResponse>('ai-mock-interview-session', {
      method: 'POST',
      body: { interviewId },
    })
  },

  saveMockInterviewSession: async (
    input: SaveMockInterviewInput
  ): Promise<{ interviewId: string; sessionId: string; saved: boolean; feedbackStatus: string }> => {
    return apiCall('ai-mock-interview-save', {
      method: 'POST',
      body: input,
    })
  },

  retryMockInterviewFeedback: async (
    interviewId: string,
    sessionId: string
  ): Promise<{ interviewId: string; sessionId: string; feedbackStatus: string }> => {
    return apiCall('ai-mock-interview-feedback', {
      method: 'POST',
      body: { interviewId, sessionId },
    })
  },

  // #199: Save coach-authored feedback notes on a specific mock interview
  // entry. `visibleToClient` controls whether the notes render on the
  // client-side portal view in addition to the staff-side review surface.
  saveMockInterviewCoachFeedback: async (
    interviewId: string,
    sessionId: string,
    notes: string,
    visibleToClient: boolean
  ): Promise<Interview> => {
    return apiCall<Interview>('save-mock-interview-coach-feedback', {
      method: 'POST',
      body: { interviewId, sessionId, notes, visibleToClient },
    })
  },

  markMockFeedbackViewed: async (
    interviewId: string,
    sessionId: string,
    viewer: 'client' | 'coach'
  ): Promise<Interview> => {
    return apiCall<Interview>('update-interview', {
      method: 'POST',
      body: { id: interviewId, markFeedbackViewed: { sessionId, viewer } },
    })
  },

  markPrepGuideViewed: async (interviewId: string): Promise<Interview> => {
    return apiCall<Interview>('update-interview', {
      method: 'POST',
      body: { id: interviewId, markPrepGuideViewed: true },
    })
  },
}

// Job Opportunities API
export interface JobOpportunity {
  id: string
  clientId: string
  clientName: string
  company: string
  jobTitle: string
  // status corresponds to the stageId of a row in jobApplicationStageDefinitions.
  // The four system-seeded defaults are applied/interviewing/offer/closed, but
  // an admin can add more columns through the Stage Gates UI.
  status: string
  applicationDate?: string
  // The client's sheet also tracks when the posting went up and when someone last
  // followed up with the employer. Both optional; distinct from
  // lastFollowUpReminderAt, which is when our own follow-up digest last nudged.
  datePosted?: string
  lastFollowUpDate?: string
  // MWA-297 — contacts staff attached by hand, on top of the ones the employer
  // -name match finds. Absent on older records; treat as empty.
  linkedContactIds?: string[]
  // How much this company matters is chosen once, on the company, in the
  // client's Dream Job target-companies list — never per-application. The
  // backend derives this from that list; the frontend only displays/filters
  // it. 'wildcard' is a company on the list that nobody ranked; 'unlisted' is
  // one that is not on the list at all. Neither is a missing value.
  companyTier: TargetCompanyTier | 'unlisted'
  // MWA-221 — free-text location shown on the card.
  location?: string
  notes?: string
  url?: string
  lastActivityDate: string
  closeReason?: string
  // MWA-220 — client decision flow. decisionRequested is set when STAFF adds the
  // app (drives the client "awaiting decision" surface); applyDecision is the
  // client's choice; not_interested sets archived. decisionHistory is the receipt.
  // MWA-304 — a seat staff believe is opening from a public signal, logged
  // before any posting exists. Mirrors the same fields on the JobOpportunity
  // in state/jobOpportunities.ts, which is a separate declaration this
  // codebase already carries; both are consumed, so both need the field.
  hiddenOpportunity?: boolean
  signalType?: string
  signalText?: string
  signalObservedDate?: string
  signalSource?: string
  vacatedBy?: string
  // MWA-355 — the client's pursue/decline answer on a hidden opportunity, with
  // who answered and when. Absent means unanswered. Must stay in step with the
  // declaration in state/jobOpportunities.ts, which this one is assigned to.
  clientPursuit?: string
  clientPursuitAt?: string
  clientPursuitBy?: string
  // MWA-421 — the Signals tables' fields. Must stay in step with the
  // declaration in state/jobOpportunities.ts, which this one is assigned to.
  watchType?: string
  companyLinkedInUrl?: string
  noPreviousHolder?: boolean
  signalAction?: string
  decisionRequested?: boolean
  applyDecision?: 'apply_on_behalf' | 'self_apply' | 'not_interested' | 'not_suitable'
  decisionHistory?: { decision: string; changedBy: string; at: string }[]
  archived?: boolean
  archivedAt?: string | null
  pendingClearedAt?: string | null
  pendingClearedBy?: string
  // MWA-224 — set-once staff confirmation that the app was actually submitted.
  // With a recorded apply-decision, this gates the protected "Applying" → "Applied" move.
  submissionConfirmed?: boolean
  submissionConfirmedAt?: string
  submissionConfirmedBy?: string
  // MWA-250 — job fit score (integer 1-5) + short rationale. Set by manual
  // entry OR the AI "Generate with AI" action (generate-job-fit-score); both
  // paths write the same fields. Renders as the inline badge in the Opportunities view.
  fitScore?: number
  fitScoreReason?: string
  fitScoreSource?: 'manual' | 'ai'
  fitScoreGeneratedAt?: string
  linkedInterviewId?: string
  statusHistory?: { status: string; timestamp: string; changedBy: string }[]
  hasDuplicate?: boolean
  createdBy?: string
  createdAt: string
  updatedAt: string
}

// Row 11 — one parsed row of a pasted application history, after the dialog has
// mapped the workbook's columns onto the fields the record actually has.
export interface ImportedJobApplication {
  company: string
  jobTitle: string
  status: 'applied' | 'following_up' | 'interviewing' | 'got_the_job' | 'rejected' | 'closed_not_accepting'
  closeReason?: string
  applicationDate?: string
  datePosted?: string
  lastFollowUpDate?: string
  location?: string
  url?: string
  notes?: string
  applyDecision?: 'apply_on_behalf' | 'self_apply' | 'not_interested' | 'not_suitable'
}

export interface JobApplicationImportResult {
  imported: number
  skippedCount: number
  skipped: Array<{ rowNumber: number; company: string; jobTitle: string; reason: string }>
}

export const jobOpportunitiesApi = {
  list: async (options?: { all?: boolean; clientId?: string }): Promise<JobOpportunity[]> => {
    const params = new URLSearchParams()
    if (options?.all) params.set('all', 'true')
    if (options?.clientId) params.set('clientId', options.clientId)
    const qs = params.toString() ? `?${params}` : ''
    return apiCall<JobOpportunity[]>(`list-job-opportunities${qs}`)
  },

  create: async (data: {
    clientId: string
    clientName?: string
    company: string
    jobTitle: string
    status?: string
    applicationDate?: string
    location?: string
    notes?: string
    url?: string
    fitScore?: number
    fitScoreReason?: string
    // MWA-297 — contacts who can open a door on this application, set at
    // creation when the job came from a referral.
    linkedContactIds?: string[]
    // MWA-297 — create with no status even for a client, because a referred job
    // has been heard about rather than applied to. See create-job-opportunity.
    awaitingDecision?: boolean
    // MWA-304 — a seat staff believe is opening from a public signal, with no
    // posting to apply to. Staff-only, enforced by create-job-opportunity.
    hiddenOpportunity?: boolean
    signalType?: string
    signalText?: string
    signalObservedDate?: string
    signalSource?: string
    vacatedBy?: string
    // MWA-421 — the Signals tables' columns, set when logging a hidden opportunity.
    watchType?: string
    companyLinkedInUrl?: string
    noPreviousHolder?: boolean
  }): Promise<JobOpportunity> => {
    return apiCall<JobOpportunity>('create-job-opportunity', { method: 'POST', body: data })
  },

  // Row 11 — bulk backfill of a client's existing application history. Same
  // endpoint as create; an `applications` array routes to the import branch, which
  // skips the client notifications and decision requests that only make sense for a
  // genuinely new opportunity. Staff-only (backend-enforced).
  import: async (data: {
    clientId: string
    clientName?: string
    applications: ImportedJobApplication[]
  }): Promise<JobApplicationImportResult> => {
    return apiCall<JobApplicationImportResult>('create-job-opportunity', { method: 'POST', body: data })
  },

  update: async (data: { id: string; [key: string]: unknown }): Promise<JobOpportunity & { createdInterview?: unknown }> => {
    return apiCall<JobOpportunity & { createdInterview?: unknown }>('update-job-opportunity', { method: 'POST', body: data })
  },

  // MWA-250 — run the configurable `job_fit_score` prompt against this
  // opportunity + the client's target profile, persisting the returned score +
  // rationale. Returns the updated opportunity. Staff-only (backend-enforced).
  generateFitScore: async (id: string): Promise<JobOpportunity> => {
    return apiCall<JobOpportunity>('generate-job-fit-score', { method: 'POST', body: { id } })
  },

  delete: async (id: string): Promise<{ id: string }> => {
    return apiCall<{ id: string }>('delete-job-opportunity', { method: 'POST', body: { id } })
  },
}

// SMS Message types
export interface SMSMessage {
  id: string
  clientId: string
  clientName?: string
  direction: 'inbound' | 'outbound'
  phoneNumber: string
  fromNumber?: string
  body: string
  status: 'queued' | 'sent' | 'delivered' | 'failed' | 'received'
  author?: string
  authorName?: string
  flowrouteMessageId?: string
  mediaUrls?: string[]
  createdAt: string
  updatedAt: string
  deliveredAt?: string
}

// SMS Message API functions
export const messagesApi = {
  list: async (clientId: string): Promise<SMSMessage[]> => {
    return apiCall<SMSMessage[]>(`list-messages?clientId=${clientId}`)
  },

  listAll: async (limit?: number): Promise<SMSMessage[]> => {
    const params = new URLSearchParams({ all: 'true' })
    if (limit) params.append('limit', limit.toString())
    return apiCall<SMSMessage[]>(`list-messages?${params.toString()}`)
  },

  send: async (clientId: string, message: string, phoneNumber?: string, mediaUrls?: string[], mediaS3Keys?: string[]): Promise<SMSMessage> => {
    return apiCall<SMSMessage>('send-sms', {
      method: 'POST',
      body: { clientId, message, phoneNumber, mediaUrls, mediaS3Keys },
    })
  },

  sendToPhone: async (phoneNumber: string, message: string, mediaUrls?: string[], mediaS3Keys?: string[]): Promise<SMSMessage> => {
    return apiCall<SMSMessage>('send-sms', {
      method: 'POST',
      body: { phoneNumber, message, mediaUrls, mediaS3Keys },
    })
  },

  linkToContact: async (
    phoneNumber: string,
    contactId: string,
    contactType: 'lead' | 'client',
    updateContactPhone?: boolean
  ): Promise<{ messagesUpdated: number; contactId: string; contactType: string; contactName: string }> => {
    return apiCall('link-messages-to-contact', {
      method: 'POST',
      body: { phoneNumber, contactId, contactType, updateContactPhone },
    })
  },
}

// Comment API functions
export const commentsApi = {
  add: async (
    entityId: string,
    content: string,
    options?: { entityType?: 'client' | 'task' | 'lead'; parentId?: string; mentions?: string[] }
  ): Promise<{ entityType: string; entityId: string; comment: Comment; totalComments: number }> => {
    return apiCall('comments', {
      method: 'POST',
      body: {
        entityId,
        content,
        entityType: options?.entityType || 'client',
        parentId: options?.parentId,
        mentions: options?.mentions,
      },
    })
  },
}

// Attachment types
export interface Attachment {
  id: string
  entityType: 'client' | 'lead' | 'resume' | 'interview' | 'north_star' | 'linkedin' | 'resume_document' | 'stars_and_stripes'
  entityId: string
  fileName: string
  contentType: string
  fileSize: number
  s3Key: string
  description?: string
  uploadedBy: string
  uploadedByName?: string
  createdAt: string
  updatedAt: string
  isActive: boolean
  isDraft?: boolean
  approved?: boolean
  approvedAt?: string
  approvedBy?: string
  internalApproved?: boolean
  internalApprovedAt?: string
  internalApprovedBy?: string
  internalApprovedByRole?: string
  sourceType?: 'file' | 'link'
  sourceUrl?: string
}

export interface GenerateUploadUrlInput {
  entityType: Attachment['entityType']
  entityId: string
  fileName: string
  contentType: string
}

export interface GenerateUploadUrlResponse {
  attachmentId: string
  uploadUrl: string
  s3Key: string
  expiresIn: number
}

export interface GenerateDownloadUrlResponse {
  downloadUrl: string
  fileName: string
  contentType: string
  fileSize: number
  expiresIn: number
}

export interface SaveAttachmentMetadataInput {
  attachmentId: string
  entityType: Attachment['entityType']
  entityId: string
  fileName: string
  contentType?: string
  fileSize?: number
  s3Key?: string
  description?: string
  isDraft?: boolean
  sourceType?: 'file' | 'link'
  sourceUrl?: string
}

export interface UpdateAttachmentInput {
  attachmentId: string
  isDraft?: boolean
  description?: string
  approved?: boolean
  internalApproved?: boolean
}

export interface NotifyClientOfUploadsResult {
  status: 'sent' | 'no_email' | 'failed'
  fileCount: number
}

// Resume API functions
export const resumesApi = {
  generate: async (
    clientId: string,
    feedback?: string,
    templateId?: string
  ): Promise<{
    status?: string
    clientId: string
    resumeContent?: string
    version?: number
  }> => {
    return apiCall('generate-resume', {
      method: 'POST',
      body: { clientId, feedback, ...(templateId ? { templateId } : {}) },
    })
  },
}

// Content publishing API (for resume and interview guides)
export const publishApi = {
  publishResume: async (clientId: string, mode: 'draft' | 'ready' = 'ready'): Promise<{ clientId: string; resumeIsDraft: boolean; resumePublishMode: 'draft' | 'ready' }> => {
    return apiCall('publish-content', {
      method: 'POST',
      body: { contentType: 'resume', entityId: clientId, publish: true, publishMode: mode },
    })
  },

  // Only while the resume is unpublished; the server refuses with 409 otherwise.
  // attachmentId designates the uploaded file, and is only valid for 'uploaded'.
  setResumeSource: async (
    clientId: string,
    source: 'ai' | 'uploaded',
    attachmentId?: string
  ): Promise<{
    clientId: string
    resumeSource: 'ai' | 'uploaded'
    resumeAttachmentId: string | null
    resumeIsDraft: boolean
    resumeStatus: NonNullable<Client['resumeStatus']>
  }> => {
    return apiCall('publish-content', {
      method: 'POST',
      body: { contentType: 'resumeSource', entityId: clientId, source, ...(attachmentId ? { attachmentId } : {}) },
    })
  },

  unpublishResume: async (clientId: string): Promise<{ clientId: string; resumeIsDraft: boolean }> => {
    return apiCall('publish-content', {
      method: 'POST',
      body: { contentType: 'resume', entityId: clientId, publish: false },
    })
  },

  approveResume: async (clientId: string): Promise<{ clientId: string; resumeApproved: boolean }> => {
    return apiCall('publish-content', {
      method: 'POST',
      body: { contentType: 'resume', entityId: clientId, approve: true },
    })
  },

  // version is the one the portal showed; the server refuses with 409 if the
  // North Star has moved on since, so the client never approves unseen content.
  approveNorthStar: async (
    clientId: string,
    version: number
  ): Promise<{ clientId: string; clientApprovedVersion: number; clientApprovedAt: string }> => {
    return apiCall('publish-content', {
      method: 'POST',
      body: { contentType: 'north_star', entityId: clientId, approve: true, version },
    })
  },

  internalApproveResume: async (clientId: string): Promise<{ clientId: string; internalResumeApproved: boolean }> => {
    return apiCall('publish-content', {
      method: 'POST',
      body: { contentType: 'resume', entityId: clientId, internalApprove: true },
    })
  },

  publishInterviewGuide: async (interviewId: string): Promise<{ interviewId: string; prepGuideIsDraft: boolean }> => {
    return apiCall('publish-content', {
      method: 'POST',
      body: { contentType: 'interviewGuide', entityId: interviewId, publish: true },
    })
  },

  unpublishInterviewGuide: async (interviewId: string): Promise<{ interviewId: string; prepGuideIsDraft: boolean }> => {
    return apiCall('publish-content', {
      method: 'POST',
      body: { contentType: 'interviewGuide', entityId: interviewId, publish: false },
    })
  },

  getResumeHistory: async (clientId: string): Promise<ActivityLogEntry[]> => {
    return apiCall('publish-content', {
      method: 'POST',
      body: { action: 'getHistory', entityId: clientId },
    })
  },
}

// Attachment API functions
export const attachmentsApi = {
  generateUploadUrl: async (data: GenerateUploadUrlInput): Promise<GenerateUploadUrlResponse> => {
    return apiCall<GenerateUploadUrlResponse>('generate-upload-url', {
      method: 'POST',
      body: data,
    })
  },

  generateDownloadUrl: async (
    attachmentId: string,
    mode: 'download' | 'preview' = 'download'
  ): Promise<GenerateDownloadUrlResponse> => {
    return apiCall<GenerateDownloadUrlResponse>(
      `generate-download-url?attachmentId=${attachmentId}&mode=${mode}`
    )
  },

  // Get a presigned URL for an MMS media file by its S3 key
  getMmsMediaUrl: async (s3Key: string): Promise<GenerateDownloadUrlResponse> => {
    return apiCall<GenerateDownloadUrlResponse>(
      `generate-download-url?s3Key=${encodeURIComponent(s3Key)}&mode=preview`
    )
  },

  saveMetadata: async (data: SaveAttachmentMetadataInput): Promise<Attachment> => {
    return apiCall<Attachment>('save-attachment-metadata', {
      method: 'POST',
      body: data,
    })
  },

  list: async (entityId: string, entityType?: Attachment['entityType']): Promise<Attachment[]> => {
    const params = new URLSearchParams({ entityId })
    if (entityType) params.append('entityType', entityType)
    return apiCall<Attachment[]>(`attachments?${params.toString()}`)
  },

  delete: async (attachmentId: string): Promise<{ attachmentId: string }> => {
    return apiCall<{ attachmentId: string }>('delete-attachment', {
      method: 'POST',
      body: { attachmentId },
    })
  },

  update: async (data: UpdateAttachmentInput): Promise<Attachment> => {
    return apiCall<Attachment>('update-attachment', {
      method: 'POST',
      body: data,
    })
  },

  // Staff only: one email to the client listing Client Folder files that just saved.
  // A missing client email or a failed send comes back in status, not as an error.
  notifyClientOfUploads: async (
    clientId: string,
    attachmentIds: string[]
  ): Promise<NotifyClientOfUploadsResult> => {
    return apiCall<NotifyClientOfUploadsResult>('update-attachment', {
      method: 'POST',
      body: { action: 'notify-client-uploads', clientId, attachmentIds },
    })
  },

  // Helper function to publish/unpublish an attachment
  publish: async (attachmentId: string): Promise<Attachment> => {
    return attachmentsApi.update({ attachmentId, isDraft: false })
  },

  unpublish: async (attachmentId: string): Promise<Attachment> => {
    return attachmentsApi.update({ attachmentId, isDraft: true })
  },

  approve: async (attachmentId: string): Promise<Attachment> => {
    return attachmentsApi.update({ attachmentId, approved: true })
  },

  internalApprove: async (attachmentId: string): Promise<Attachment> => {
    return attachmentsApi.update({ attachmentId, internalApproved: true })
  },

  // Helper function to upload a file (combines generateUploadUrl, actual upload, and saveMetadata)
  upload: async (
    file: File,
    entityType: Attachment['entityType'],
    entityId: string,
    description?: string,
    isDraft?: boolean
  ): Promise<Attachment> => {
    // 1. Generate presigned upload URL
    const uploadUrlResponse = await attachmentsApi.generateUploadUrl({
      entityType,
      entityId,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
    })

    // 2. Upload file directly to S3 using the presigned URL
    const uploadResponse = await fetch(uploadUrlResponse.uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    })

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file to storage')
    }

    // 3. Save attachment metadata to DynamoDB
    const attachment = await attachmentsApi.saveMetadata({
      attachmentId: uploadUrlResponse.attachmentId,
      entityType,
      entityId,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      fileSize: file.size,
      s3Key: uploadUrlResponse.s3Key,
      description,
      isDraft,
    })

    return attachment
  },

  // Add an external link (Google Drive, Dropbox, etc.) as an attachment
  addLink: async (
    entityType: Attachment['entityType'],
    entityId: string,
    url: string,
    fileName: string,
    description?: string,
    isDraft?: boolean
  ): Promise<Attachment> => {
    const attachmentId = crypto.randomUUID()
    return attachmentsApi.saveMetadata({
      attachmentId,
      entityType,
      entityId,
      fileName,
      sourceType: 'link',
      sourceUrl: url,
      description,
      isDraft,
    })
  },

  // Helper function to download a file
  download: async (attachmentId: string): Promise<void> => {
    const { downloadUrl, fileName } = await attachmentsApi.generateDownloadUrl(
      attachmentId,
      'download'
    )

    // Create a temporary link and trigger download. No target="_blank": the
    // response's Content-Disposition: attachment forces a download without
    // navigating away, so opening a new tab is unnecessary and just leaves a
    // blank "Untitled" tab behind (MWA-272).
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  },

  // Helper function to preview/open a file in a new tab
  preview: async (attachmentId: string): Promise<void> => {
    const { downloadUrl } = await attachmentsApi.generateDownloadUrl(attachmentId, 'preview')

    // Open in new tab for inline viewing
    window.open(downloadUrl, '_blank')
  },
}

// Product API functions
export const productsApi = {
  list: async (options?: { status?: Product['status'] | 'all'; type?: Product['type'] }): Promise<Product[]> => {
    const params = new URLSearchParams()
    if (options?.status) params.append('status', options.status)
    if (options?.type) params.append('type', options.type)
    const queryString = params.toString()
    return apiCall<Product[]>(`list-products${queryString ? `?${queryString}` : ''}`)
  },

  create: async (data: CreateProductInput): Promise<Product> => {
    return apiCall<Product>('manage-product', { method: 'POST', body: data })
  },

  update: async (data: UpdateProductInput): Promise<Product> => {
    return apiCall<Product>('manage-product', { method: 'PUT', body: data })
  },

  delete: async (id: string): Promise<Product> => {
    return apiCall<Product>('manage-product', { method: 'DELETE', body: { id } })
  },
}

// Package API functions
export const packagesApi = {
  list: async (options?: { status?: Package['status'] | 'all' }): Promise<Package[]> => {
    const params = new URLSearchParams()
    if (options?.status) params.append('status', options.status)
    const queryString = params.toString()
    return apiCall<Package[]>(`list-packages${queryString ? `?${queryString}` : ''}`)
  },

  create: async (data: CreatePackageInput): Promise<Package> => {
    return apiCall<Package>('manage-package', { method: 'POST', body: data })
  },

  update: async (data: UpdatePackageInput): Promise<Package> => {
    return apiCall<Package>('manage-package', { method: 'PUT', body: data })
  },

  delete: async (id: string): Promise<Package> => {
    return apiCall<Package>('manage-package', { method: 'DELETE', body: { id } })
  },
}

// Portal Dashboard types
export interface PortalDashboardData {
  client: {
    id: string
    name: string
    email: string
    workflowStage: string
    leadId?: string
  }
  formCompletion: {
    intake: { completed: boolean; lastUpdated: string | null }
    dreamJob: { completed: boolean; lastUpdated: string | null }
    cliftonStrengths: { completed: boolean; lastUpdated: string | null }
  }
  contentAvailability: PortalContentAvailability
  progress: {
    formsCompleted: number
    totalForms: number
    percentComplete: number
  }
  pendingTaskCount: number
  tasks: Array<{
    id: string
    title: string
    description?: string
    dueDate?: string
    status: string
    priority: string
  }>
  resume: {
    id: string
    status: string
    version: number
    lastUpdated: string
  } | null
}

// Portal API functions
export const portalApi = {
  getDashboard: async (): Promise<PortalDashboardData> => {
    return apiCall<PortalDashboardData>('portal-dashboard')
  },

  // Create portal user for a client (staff-only)
  createClientUser: async (
    clientId: string,
    email?: string
  ): Promise<{ client: Client; portalEmail: string }> => {
    return apiCall<{ client: Client; portalEmail: string }>('create-client-user', {
      method: 'POST',
      body: { clientId, email },
    })
  },
}

// Global Search types
export interface SearchResult {
  id: string
  entityType: 'lead' | 'client' | 'task' | 'contact' | 'jobapplication'
  score: number
  data: Record<string, unknown>
  highlights: Record<string, string[]>
}

export interface GlobalSearchResponse {
  query: string
  total: number
  results: SearchResult[]
  grouped: {
    leads: SearchResult[]
    clients: SearchResult[]
    tasks: SearchResult[]
    contacts: SearchResult[]
    jobApplications: SearchResult[]
  }
  /**
   * What each type actually matched, which is not what was returned: each type
   * is capped so no single loud type can crowd the others out (MWA-336). The
   * group headers read "5 of 36" from this. Optional because a response from
   * before that change will not carry it.
   */
  groupedTotals?: {
    leads: number
    clients: number
    tasks: number
    contacts: number
    jobApplications: number
  }
}

// Global Search API functions
export const searchApi = {
  globalSearch: async (
    query: string,
    limit: number = 20,
    types: string[] = ['leads', 'clients', 'tasks', 'contacts', 'jobapplications']
  ): Promise<GlobalSearchResponse> => {
    return apiCall<GlobalSearchResponse>('search', {
      method: 'POST',
      body: { query, limit, types },
    })
  },
}

// Stage Gate types
export type TeamRole = 'sales' | 'clientSuccess' | 'resumeWriter' | 'coach'

export interface StageGateTask {
  id: string
  title: string
  description?: string
  required: boolean
  sortOrder: number
  ownerRole?: TeamRole
}

export interface StageGate {
  stageId: string
  tasks: StageGateTask[]
  allowOverride: boolean
  updatedAt?: string
  updatedBy?: string
}

export interface ClientStageTask {
  gateTaskId: string
  taskId?: string // New task ID in unified tasks table
  title: string
  description?: string
  required: boolean
  completed: boolean
  completedBy?: string
  completedAt?: string
  notes?: string
  assignedTo?: string
}

export interface ClientStageTasks {
  id: string
  clientId: string
  stageId: string
  tasks: ClientStageTask[]
  allRequiredComplete: boolean
  overrideUsed: boolean
  overrideBy?: string
  overrideAt?: string
  overrideReason?: string
}

// Stage Gates API functions
export const stageGatesApi = {
  list: async (): Promise<StageGate[]> => {
    return apiCall<StageGate[]>('stage-gates')
  },

  get: async (stageId: string): Promise<StageGate> => {
    return apiCall<StageGate>(`stage-gates?stageId=${stageId}`)
  },

  update: async (stageGate: StageGate): Promise<StageGate> => {
    return apiCall<StageGate>('manage-stage-gates', {
      method: 'POST',
      body: stageGate,
    })
  },

  deleteTask: async (stageId: string, taskId: string): Promise<StageGate> => {
    return apiCall<StageGate>('manage-stage-gates', {
      method: 'DELETE',
      body: { stageId, taskId },
    })
  },
}

// Client Stage Tasks API functions
export const clientStageTasksApi = {
  get: async (clientId: string, stageId?: string): Promise<ClientStageTasks | ClientStageTasks[]> => {
    const params = new URLSearchParams({ clientId })
    if (stageId) params.append('stageId', stageId)
    return apiCall<ClientStageTasks | ClientStageTasks[]>(`client-stage-tasks?${params.toString()}`)
  },

  completeTask: async (
    clientId: string,
    stageId: string,
    gateTaskId: string,
    completed: boolean,
    notes?: string,
    taskId?: string // New task ID for unified tasks table
  ): Promise<ClientStageTasks> => {
    return apiCall<ClientStageTasks>('client-stage-tasks', {
      method: 'POST',
      body: { clientId, stageId, gateTaskId, taskId, completed, notes },
    })
  },
}

// Workflow Stages types
export interface WorkflowStageDefinition {
  stageId: string
  label: string
  color: string
  textColor: string
  teamRole: string | null
  sortOrder: number
  isActive: boolean
  isSystem: boolean
  hiddenPanels?: string[]
  // MWA-391 — the client email sent when a client enters this stage, or null/absent for none.
  entryEmail?: string | null
  createdAt?: string
  updatedAt?: string
  createdBy?: string
  updatedBy?: string
}

// Workflow Stages API functions
export const workflowStagesApi = {
  list: async (forceRefresh = false): Promise<WorkflowStageDefinition[]> => {
    const endpoint = forceRefresh ? 'workflow-stages?refresh=true' : 'workflow-stages'
    return apiCall<WorkflowStageDefinition[]>(endpoint)
  },

  create: async (data: {
    label: string
    color: string
    teamRole: string | null
    insertAfter?: string
    entryEmail?: string | null
  }): Promise<WorkflowStageDefinition> => {
    return apiCall<WorkflowStageDefinition>('manage-workflow-stages', {
      method: 'POST',
      body: data,
    })
  },

  update: async (
    stageId: string,
    data: {
      label: string
      color: string
      teamRole: string | null
      isSystem?: boolean
      hiddenPanels?: string[]
      entryEmail?: string | null
    }
  ): Promise<WorkflowStageDefinition> => {
    return apiCall<WorkflowStageDefinition>('manage-workflow-stages', {
      method: 'PUT',
      body: { stageId, ...data },
    })
  },

  delete: async (stageId: string): Promise<void> => {
    return apiCall<void>('manage-workflow-stages', {
      method: 'DELETE',
      body: { stageId },
    })
  },

  reorder: async (stageIds: string[]): Promise<{ stages: WorkflowStageDefinition[]; updated: number }> => {
    return apiCall<{ stages: WorkflowStageDefinition[]; updated: number }>('reorder-workflow-stages', {
      method: 'POST',
      body: { stageIds },
    })
  },
}

// Sales Stage Definition (same structure as WorkflowStageDefinition but with showValue)
export interface SalesStageDefinition extends Omit<WorkflowStageDefinition, 'isSystem' | 'isActive'> {
  showValue: boolean
  isSystem: boolean
  isActive: boolean
}

// Interview Stage Definition (same structure as WorkflowStageDefinition)
export interface InterviewStageDefinition {
  stageId: string
  label: string
  color: string
  textColor: string
  teamRole: string | null
  sortOrder: string | number
  isActive: boolean
  isSystem: boolean
  createdAt?: string
  updatedAt?: string
  createdBy?: string
  updatedBy?: string
}

// Sales Stages API functions
export const salesStagesApi = {
  list: async (forceRefresh = false): Promise<SalesStageDefinition[]> => {
    const endpoint = forceRefresh ? 'sales-stages?refresh=true' : 'sales-stages'
    return apiCall<SalesStageDefinition[]>(endpoint)
  },

  create: async (data: {
    label: string
    color: string
    teamRole: string | null
    insertAfter?: string
    showValue?: boolean
  }): Promise<SalesStageDefinition> => {
    return apiCall<SalesStageDefinition>('manage-sales-stages', {
      method: 'POST',
      body: data,
    })
  },

  update: async (
    stageId: string,
    data: {
      label: string
      color: string
      teamRole: string | null
      showValue?: boolean
      isSystem?: boolean
    }
  ): Promise<SalesStageDefinition> => {
    return apiCall<SalesStageDefinition>('manage-sales-stages', {
      method: 'PUT',
      body: { stageId, ...data },
    })
  },

  delete: async (stageId: string): Promise<void> => {
    return apiCall<void>('manage-sales-stages', {
      method: 'DELETE',
      body: { stageId },
    })
  },

  reorder: async (stageIds: string[]): Promise<{ stages: SalesStageDefinition[]; updated: number }> => {
    return apiCall<{ stages: SalesStageDefinition[]; updated: number }>('reorder-sales-stages', {
      method: 'POST',
      body: { stageIds },
    })
  },
}

// Interview Stages API functions
export const interviewStagesApi = {
  list: async (forceRefresh = false): Promise<InterviewStageDefinition[]> => {
    const endpoint = forceRefresh ? 'interview-stages?refresh=true' : 'interview-stages'
    return apiCall<InterviewStageDefinition[]>(endpoint)
  },

  create: async (data: {
    label: string
    color: string
    teamRole: string | null
    insertAfter?: string
  }): Promise<InterviewStageDefinition> => {
    return apiCall<InterviewStageDefinition>('manage-interview-stages', {
      method: 'POST',
      body: data,
    })
  },

  update: async (
    stageId: string,
    data: {
      label: string
      color: string
      teamRole: string | null
      isSystem?: boolean
    }
  ): Promise<InterviewStageDefinition> => {
    return apiCall<InterviewStageDefinition>('manage-interview-stages', {
      method: 'PUT',
      body: { stageId, ...data },
    })
  },

  delete: async (stageId: string): Promise<void> => {
    return apiCall<void>('manage-interview-stages', {
      method: 'DELETE',
      body: { stageId },
    })
  },

  reorder: async (stageIds: string[]): Promise<{ stages: InterviewStageDefinition[]; updated: number }> => {
    return apiCall<{ stages: InterviewStageDefinition[]; updated: number }>('reorder-interview-stages', {
      method: 'POST',
      body: { stageIds },
    })
  },
}

// Job Application Stage Definition — identical to the other three pipeline
// stage shapes minus teamRole. Job-application columns are client-driven and
// don't have a firm-side role owner, so the backend never persists teamRole.
export interface JobApplicationStageDefinition {
  stageId: string
  label: string
  color: string
  textColor: string
  sortOrder: string | number
  isActive: boolean
  isSystem: boolean
  createdAt?: string
  updatedAt?: string
  createdBy?: string
  updatedBy?: string
}

// Job Application Stages API functions
export const jobApplicationStagesApi = {
  list: async (forceRefresh = false): Promise<JobApplicationStageDefinition[]> => {
    const endpoint = forceRefresh ? 'job-application-stages?refresh=true' : 'job-application-stages'
    return apiCall<JobApplicationStageDefinition[]>(endpoint)
  },

  create: async (data: {
    label: string
    color: string
    insertAfter?: string
  }): Promise<JobApplicationStageDefinition> => {
    return apiCall<JobApplicationStageDefinition>('manage-job-application-stages', {
      method: 'POST',
      body: data,
    })
  },

  update: async (
    stageId: string,
    data: {
      label?: string
      color?: string
      isSystem?: boolean
    }
  ): Promise<JobApplicationStageDefinition> => {
    return apiCall<JobApplicationStageDefinition>('manage-job-application-stages', {
      method: 'PUT',
      body: { stageId, ...data },
    })
  },

  delete: async (stageId: string): Promise<void> => {
    return apiCall<void>('manage-job-application-stages', {
      method: 'DELETE',
      body: { stageId },
    })
  },

  reorder: async (
    stageIds: string[]
  ): Promise<{ stages: JobApplicationStageDefinition[]; updated: number }> => {
    return apiCall<{ stages: JobApplicationStageDefinition[]; updated: number }>(
      'reorder-job-application-stages',
      {
        method: 'POST',
        body: { stageIds },
      }
    )
  },
}

// AI Prompt types
export type AIPromptCategory = 'resume' | 'linkedin' | 'interview' | 'proposal' | 'other'

export interface AIPromptVersion {
  version: number
  systemPrompt: string
  instructions: string
  updatedAt: string
  updatedBy: string
}

// Output format can be a JSON schema object or a string description
export type AIPromptOutputFormat = Record<string, unknown> | string

export interface AIPrompt {
  promptId: string
  name: string
  description: string
  category: AIPromptCategory
  // EDITABLE fields - users can customize these
  systemPrompt: string // AI personality and approach
  instructions: string // Generation guidance, tone, style
  model: string
  maxTokens: number
  isActive: boolean
  // PROTECTED fields - read-only, cannot be edited (would break app)
  outputFormat: AIPromptOutputFormat // JSON schema the app parses
  dataTemplate: string // Where {{variables}} get inserted
  variables: string[] // System-defined data fields
  // Metadata
  version: number
  isDefault?: boolean // True if using default prompt (not customized)
  history?: AIPromptVersion[]
  updatedAt?: string
  updatedBy?: string
  createdAt?: string
  createdBy?: string
}

// Only editable fields are allowed in create/update input
export interface CreateAIPromptInput {
  promptId: string
  name?: string
  description?: string
  category?: AIPromptCategory
  systemPrompt: string // Required - AI personality
  instructions: string // Required - Generation guidance
  outputFormat?: string // Optional - JSON output schema (stored as parsed JSON)
  model?: string
  maxTokens?: number
  isActive?: boolean
}

// AI Prompts API functions
export const aiPromptsApi = {
  list: async (): Promise<AIPrompt[]> => {
    return apiCall<AIPrompt[]>('ai-prompts')
  },

  create: async (data: CreateAIPromptInput): Promise<AIPrompt> => {
    return apiCall<AIPrompt>('manage-ai-prompts', {
      method: 'POST',
      body: data,
    })
  },

  update: async (data: CreateAIPromptInput): Promise<AIPrompt> => {
    return apiCall<AIPrompt>('manage-ai-prompts', {
      method: 'POST',
      body: data,
    })
  },

  resetToDefault: async (promptId: string): Promise<{ promptId: string }> => {
    return apiCall<{ promptId: string }>('manage-ai-prompts', {
      method: 'DELETE',
      body: { promptId },
    })
  },
}

// Company Context — brand identity / voice / offerings / process used by AI agents and MCP server

export type CompanyContextSectionId =
  | 'brand-identity'
  | 'brand-voice'
  | 'service-offerings'
  | 'process'

export interface CompanyContextVersion {
  version: number
  content: string
  updatedAt: string
  updatedBy: string
}

export interface CompanyContextSection {
  sectionId: CompanyContextSectionId
  title: string
  content: string
  order: number
  isActive: boolean
  version: number
  isEmpty?: boolean
  history?: CompanyContextVersion[]
  updatedAt?: string
  updatedBy?: string
  createdAt?: string
  createdBy?: string
}

export interface SaveCompanyContextInput {
  sectionId: CompanyContextSectionId
  title?: string
  content: string
  isActive?: boolean
}

export const companyContextApi = {
  list: async (): Promise<CompanyContextSection[]> => {
    return apiCall<CompanyContextSection[]>('company-context')
  },

  update: async (data: SaveCompanyContextInput): Promise<CompanyContextSection> => {
    return apiCall<CompanyContextSection>('manage-company-context', {
      method: 'POST',
      body: data,
    })
  },

  clear: async (sectionId: CompanyContextSectionId): Promise<{ sectionId: CompanyContextSectionId }> => {
    return apiCall<{ sectionId: CompanyContextSectionId }>('manage-company-context', {
      method: 'DELETE',
      body: { sectionId },
    })
  },
}

// North Star — configurable, AI-drafted, coach-polished shareable career strategy microsite

export type NorthStarRenderType =
  | 'hero'
  | 'narrative'
  | 'comparison'
  | 'deepDive'
  | 'chartCompare'
  | 'chartTrajectory'
  | 'tagGrid'
  | 'decision'
  | 'actionCards'

export type NorthStarFieldType =
  | 'text'
  | 'richText'
  | 'number'
  | 'numberRange'
  | 'list'
  | 'enum'
  | 'dimensions'
  | 'trajectory'
  | 'repeater'

export interface NorthStarFieldDef {
  id: string
  label: string
  type: NorthStarFieldType
  required?: boolean
  options?: string[]
  axes?: string[]
  years?: number
  min?: number
  max?: number
  fields?: NorthStarFieldDef[]
}

export interface NorthStarSectionTemplate {
  sectionTypeId: string
  name: string
  description?: string
  renderType: NorthStarRenderType
  fields: NorthStarFieldDef[]
  aiPromptSnippet?: string
  defaultOrder: number
  defaultEnabled: boolean
  isActive: boolean
  version: number
  updatedAt?: string
  updatedBy?: string
}

export interface NorthStarSectionInstance {
  sectionTypeId: string
  order: number
  enabled: boolean
  fieldValues: Record<string, unknown>
  /** Per-client override of the section's displayed heading (falls back to the template's name). */
  titleOverride?: string
  /** Per-client override of the section's sub-description (falls back to the template's description). */
  descriptionOverride?: string
}

export interface NorthStarRecord {
  clientId: string
  slug?: string
  isPublished: boolean
  publishedAt?: string
  unpublishedAt?: string
  sections: NorthStarSectionInstance[]
  meta?: {
    generatedAt?: string
    compDisclaimer?: string
    templateVersions?: Record<string, number>
  }
  status?: 'generating' | 'ready' | 'error'
  errorMessage?: string
  generationStartedAt?: string
  version: number
  history?: unknown[]
  updatedAt?: string
  updatedBy?: string
  // The client's sign-off, valid only while clientApprovedVersion equals
  // version on a published record (see utils/northStarApproval).
  clientApprovedVersion?: number
  clientApprovedAt?: string
  clientApprovedBy?: string
}

export interface NorthStarViewRow {
  slug: string
  viewedAt: string
  truncatedIp?: string
  userAgent?: string
  referer?: string
}

export interface GetNorthStarResponse {
  northStar: NorthStarRecord | null
  templates: Record<string, NorthStarSectionTemplate>
  recentViews: NorthStarViewRow[]
  viewCount: number
}

export interface PublicNorthStarResponse {
  slug: string
  isPublished: boolean
  publishedAt?: string
  client: { firstName: string; name: string; currentRole: string } | null
  sections: NorthStarSectionInstance[]
  templates: Record<string, NorthStarSectionTemplate>
  meta: NorthStarRecord['meta']
}

export interface MissingDataError {
  error: string
  missing: string[]
  message: string
  links?: Record<string, string>
}

export const northStarTemplatesApi = {
  list: async (): Promise<NorthStarSectionTemplate[]> => {
    return apiCall<NorthStarSectionTemplate[]>('north-star-templates')
  },
  save: async (template: Partial<NorthStarSectionTemplate>): Promise<NorthStarSectionTemplate> => {
    return apiCall<NorthStarSectionTemplate>('manage-north-star-templates', {
      method: 'POST',
      body: template,
    })
  },
  deactivate: async (sectionTypeId: string): Promise<{ sectionTypeId: string }> => {
    return apiCall<{ sectionTypeId: string }>('manage-north-star-templates', {
      method: 'DELETE',
      body: { sectionTypeId },
    })
  },
  seed: async (force = false): Promise<{ created: string[]; skipped: string[]; updated: string[] }> => {
    const qs = force ? '?force=true' : ''
    return apiCall(`seed-north-star-templates${qs}`, { method: 'POST', body: {} })
  },
}

export const northStarApi = {
  get: async (clientId: string): Promise<GetNorthStarResponse | null> => {
    return apiCall<GetNorthStarResponse | null>(`north-star?clientId=${encodeURIComponent(clientId)}`)
  },
  generate: async (clientId: string): Promise<{ status: string; clientId: string; version: number }> => {
    return apiCall(`generate-north-star`, { method: 'POST', body: { clientId } })
  },
  save: async (input: {
    clientId: string
    sections: NorthStarSectionInstance[]
    meta?: NorthStarRecord['meta']
  }): Promise<NorthStarRecord> => {
    return apiCall<NorthStarRecord>('save-north-star', { method: 'POST', body: input })
  },
  publish: async (input: {
    clientId: string
    unpublish?: boolean
    rotateSlug?: boolean
  }): Promise<Pick<NorthStarRecord, 'clientId' | 'slug' | 'isPublished' | 'publishedAt' | 'unpublishedAt'>> => {
    return apiCall('publish-north-star', { method: 'POST', body: input })
  },
}

// Public — unauthenticated calls used by the shareable page
export const publicNorthStarApi = {
  getBySlug: async (slug: string, preview = false): Promise<PublicNorthStarResponse> => {
    const previewQs = preview ? '&preview=1' : ''
    return publicApiCall<PublicNorthStarResponse>(`public-north-star?slug=${encodeURIComponent(slug)}${previewQs}`)
  },
  logView: async (slug: string): Promise<void> => {
    try {
      await publicApiCall<void>('log-north-star-view', { method: 'POST', body: { slug } })
    } catch {
      // fire-and-forget — never block the page on view logging
    }
  },
}

// Prompt Templates — user-created named resume templates
export interface TemplateSection {
  id: string
  name: string
  instruction: string
}

export interface PromptTemplate {
  id: string
  name: string
  description?: string
  category: string
  instructions: string
  sections?: TemplateSection[]
  isActive: boolean
  isDefault: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
  updatedBy?: string
}

export interface SavePromptTemplateInput {
  id?: string
  name: string
  description?: string
  category: string
  instructions: string
  sections?: TemplateSection[]
  isDefault?: boolean
  createdAt?: string
  createdBy?: string
}

export const promptTemplatesApi = {
  list: async (category = 'resume'): Promise<PromptTemplate[]> => {
    return apiCall<PromptTemplate[]>(`prompt-templates?category=${category}`)
  },

  save: async (data: SavePromptTemplateInput): Promise<PromptTemplate> => {
    return apiCall<PromptTemplate>('manage-prompt-templates', {
      method: 'POST',
      body: data,
    })
  },

  delete: async (id: string): Promise<{ id: string }> => {
    return apiCall<{ id: string }>('manage-prompt-templates', {
      method: 'DELETE',
      body: { id },
    })
  },
}

// Email attachment metadata
export interface EmailAttachment {
  id: string
  fileName: string
  contentType: string
  fileSize: number
  s3Key: string
}

// Email types
export interface Email {
  id: string
  clientId: string
  clientName?: string
  direction: 'inbound' | 'outbound'
  email: string
  fromEmail?: string
  toEmail?: string
  /** Present only when one email was sent to several addresses; `email` is then the first of them. */
  toEmails?: string[]
  subject: string
  htmlBody?: string
  textBody?: string
  status: 'queued' | 'sent' | 'delivered' | 'failed' | 'received'
  author?: string
  authorName?: string
  sendgridMessageId?: string
  isRead?: boolean
  readAt?: string
  readBy?: string
  isStarred?: boolean
  isArchived?: boolean
  isDeleted?: boolean
  attachments?: EmailAttachment[]
  createdAt: string
  updatedAt: string
}

// AI Generated Email response
export interface GeneratedEmail {
  subject: string
  body: string
  tone: string
}

// Email API functions
export const emailsApi = {
  list: async (clientId: string): Promise<Email[]> => {
    return apiCall<Email[]>(`list-emails?clientId=${clientId}`)
  },

  listAll: async (limit?: number): Promise<Email[]> => {
    const params = new URLSearchParams({ all: 'true' })
    if (limit) params.append('limit', limit.toString())
    return apiCall<Email[]>(`list-emails?${params.toString()}`)
  },

  send: async (
    clientId: string,
    subject: string,
    body: string,
    attachmentIds?: string[]
  ): Promise<Email> => {
    return apiCall<Email>('send-email', {
      method: 'POST',
      body: { clientId, subject, body, attachmentIds },
    })
  },

  sendToEmail: async (
    email: string,
    subject: string,
    body: string,
    attachmentIds?: string[]
  ): Promise<Email> => {
    return apiCall<Email>('send-email', {
      method: 'POST',
      body: { email, subject, body, attachmentIds },
    })
  },

  generateEmail: async (
    clientId: string | null,
    purpose: string,
    options?: {
      context?: string
      existingDraft?: string
      recentActivity?: string
      recipientName?: string
    }
  ): Promise<GeneratedEmail> => {
    return apiCall<GeneratedEmail>('generate-email', {
      method: 'POST',
      body: {
        ...(clientId ? { clientId } : {}),
        purpose,
        context: options?.context,
        existingDraft: options?.existingDraft,
        recentActivity: options?.recentActivity,
        recipientName: options?.recipientName,
      },
    })
  },

  markAsRead: async (emailId: string): Promise<void> => {
    await apiCall<void>('mark-email-read', {
      method: 'POST',
      body: { emailId },
    })
  },

  bulkUpdate: async (
    emailIds: string[],
    updates: { isRead?: boolean; isStarred?: boolean; isArchived?: boolean; isDeleted?: boolean }
  ): Promise<{ updated: number; failed: number }> => {
    return apiCall<{ updated: number; failed: number }>('bulk-update-emails', {
      method: 'POST',
      body: { emailIds, updates },
    })
  },
}

// Settings types
export interface Setting {
  key: string
  value: string | null
  description?: string
  updatedBy?: string
  updatedAt?: string
}

// Settings API functions
export const settingsApi = {
  list: async (): Promise<Setting[]> => {
    return apiCall<Setting[]>('list-settings')
  },

  get: async (key: string): Promise<Setting> => {
    return apiCall<Setting>(`list-settings?key=${key}`)
  },

  update: async (key: string, value: string): Promise<Setting> => {
    return apiCall<Setting>('manage-settings', {
      method: 'POST',
      body: { key, value },
    })
  },

  reset: async (key: string): Promise<{ key: string }> => {
    return apiCall<{ key: string }>('manage-settings', {
      method: 'DELETE',
      body: { key },
    })
  },
}

// Contact types
export interface Contact {
  id: string
  clientId: string
  firstName: string
  lastName: string
  fullName: string
  company: string
  position?: string
  linkedinUrl?: string
  email?: string
  // Phone number for the contact. Optional — most networking contacts come
  // in from LinkedIn imports which don't carry phone, but Apollo-enriched
  // and manually-entered contacts may have one. Drives the "Send SMS"
  // prefill in OutreachTemplateDialog (sms: handoff to the client's own
  // messaging app — Lopie never sees the actual SMS traffic).
  phone?: string
  connectedOn?: string
  // Absent means nobody has reached out yet. All three statuses describe an
  // outreach that happened, so there is no value for the pre-outreach state.
  status?: ContactStatus
  connectionType?: ConnectionType
  isPriority: boolean
  source: 'linkedin_import' | 'manual'
  notes?: string
  referredBy?: string
  importBatchId?: string
  isDeleted?: boolean
  // Per-contact outreach checklist progress, keyed first by stage (contactStatus)
  // then by item text. See OutreachChecklist.tsx and DEFECT 1213986798255599.
  checklistProgress?: Record<string, Record<string, boolean>>
  // The strategist's assignment of this contact to the client: when it was
  // assigned (ISO timestamp) and why. reachOutAt is NOT a deadline for the
  // client to make contact by — coaches cannot dictate that — it is the
  // hand-off date accountability is measured from (MWA-353). The attribute
  // name predates that clarification; the UI reads "Assigned on".
  // Staff write them, the client only reads
  // them — update-contact drops both from a client's update the same way it
  // drops isPriority. Absent on every contact created before they existed; no
  // migration was run. Deliberately not tied to any reminder: the outreach
  // log's own followUpDueAt already drives the daily digest, and a second
  // timing source could disagree with it about when a contact is due.
  reachOutAt?: string | null
  reachOutReason?: string
  createdAt: string
  updatedAt: string
}

export interface ContactInteraction {
  id: string
  // Exactly one of contactId/interviewId is ever present — contactId for the
  // networking flow, interviewId for the interview follow-up (hiring-side)
  // flow. Enforced server-side in new-contact-interaction.
  contactId?: string
  interviewId?: string
  clientId: string
  // Retired values (message_sent, outreach_sent, note) are still present on
  // historical records and deliberately not migrated, so this stays a string
  // rather than narrowing to InteractionType and lying about what can be read.
  type: string
  notes?: string
  // MWA-297 — when the conversation happened, which is not always when the row
  // was written. Absent on records created before this field existed; read it
  // through interactionDate() so those fall back to createdAt.
  occurredAt?: string
  // Set by log-outreach on the interaction a send writes, pointing at that
  // send's outreach-log entry. Absent on manual interactions and on anything
  // logged before the link existed.
  outreachLogId?: string
  createdAt: string
  createdBy: string
}

export interface ImportContactsResult {
  imported: number
  skippedDuplicates: number
  batchId: string
}

// Contacts API functions
export const contactsApi = {
  list: async (clientId: string, options?: { status?: string; company?: string }): Promise<Contact[]> => {
    const params = new URLSearchParams({ clientId })
    if (options?.status) params.set('status', options.status)
    if (options?.company) params.set('company', options.company)
    return apiCall<Contact[]>(`list-contacts?${params}`)
  },

  /**
   * `force` skips the duplicate check (MWA-326). new-contact answers 409 with
   * the matching contact when one looks like a duplicate; the caller decides
   * whether it really is, and re-sends with force to go ahead.
   */
  create: async (data: Partial<Contact> & { force?: boolean }): Promise<Contact> => {
    return apiCall<Contact>('new-contact', { method: 'POST', body: data })
  },

  update: async (data: Partial<Contact> & { id: string }): Promise<Contact> => {
    return apiCall<Contact>('update-contact', { method: 'POST', body: data })
  },

  delete: async (id: string): Promise<void> => {
    await apiCall<void>('delete-contact', { method: 'POST', body: { id } })
  },

  import: async (contacts: Partial<Contact>[], clientId: string): Promise<ImportContactsResult> => {
    return apiCall<ImportContactsResult>('import-contacts', {
      method: 'POST',
      body: { contacts, clientId },
    })
  },
}

// Contact Interactions API functions
export const contactInteractionsApi = {
  list: async (params: { contactId?: string; interviewId?: string; clientId?: string }): Promise<ContactInteraction[]> => {
    const qs = new URLSearchParams()
    if (params.contactId) qs.set('contactId', params.contactId)
    if (params.interviewId) qs.set('interviewId', params.interviewId)
    if (params.clientId) qs.set('clientId', params.clientId)
    return apiCall<ContactInteraction[]>(`list-contact-interactions?${qs}`)
  },

  // Exactly one of contactId/interviewId must be supplied — validated server-side.
  create: async (data: { contactId?: string; interviewId?: string; type: string; notes?: string; occurredAt?: string }): Promise<ContactInteraction> => {
    return apiCall<ContactInteraction>('new-contact-interaction', { method: 'POST', body: data })
  },
}

// Outreach Checklists
export interface OutreachChecklist {
  id: string
  stage: OutreachStage
  title: string
  items: string[]
  isDefault: boolean
  clientId: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

export const outreachChecklistsApi = {
  manage: async (data: { action: string } & Record<string, unknown>): Promise<OutreachChecklist | OutreachChecklist[] | { id: string }> => {
    return apiCall('manage-outreach-checklists', { method: 'POST', body: data })
  },

  get: async (stage: string, clientId?: string): Promise<OutreachChecklist | null> => {
    const params = new URLSearchParams({ stage })
    if (clientId) params.set('clientId', clientId)
    return apiCall<OutreachChecklist | null>(`get-outreach-checklist?${params}`)
  },
}

// ──────────────────────────────────────
// Outreach Templates
// ──────────────────────────────────────

export interface OutreachTemplateCategory {
  id: string
  name: string
  slug: string
  sortOrder: number
  isActive: boolean
  isDeleted?: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface OutreachTemplate {
  id: string
  categoryId: string
  title: string
  body: string
  mergeFields: string[]
  talkingPoints: string[]
  seniorityLevel?: 'junior' | 'mid' | 'senior' | 'director' | 'executive' | null
  sortOrder: number
  isActive: string
  isDefault: boolean
  clientId: string
  version: number
  previousVersions?: { version: number; body: string; updatedAt: string; updatedBy: string }[]
  assignedStages?: string[]
  isDeleted?: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface OutreachLogEntry {
  id: string
  contactId: string
  clientId: string
  templateId: string
  templateVersion: number | null
  categoryId: string | null
  resolvedBody: string
  sentAt: string
  sentBy: string
  sentByName: string
  followUpDueAt: string
  followUpStatus: 'pending' | 'completed' | 'dismissed'
  followUpCompletedAt?: string | null
  isDeleted?: boolean
  createdBy: string
}

export const outreachTemplatesApi = {
  manage: async (data: { action: string } & Record<string, unknown>): Promise<unknown> => {
    return apiCall('manage-outreach-templates', { method: 'POST', body: data })
  },

  list: async (params?: { categoryId?: string; seniorityLevel?: string }): Promise<{ categories: OutreachTemplateCategory[]; templates: OutreachTemplate[] }> => {
    const qs = new URLSearchParams()
    if (params?.categoryId) qs.set('categoryId', params.categoryId)
    if (params?.seniorityLevel) qs.set('seniorityLevel', params.seniorityLevel)
    const query = qs.toString()
    return apiCall<{ categories: OutreachTemplateCategory[]; templates: OutreachTemplate[] }>(`list-outreach-templates${query ? `?${query}` : ''}`)
  },
}

export const outreachLogApi = {
  send: async (data: {
    contactId: string
    templateId: string
    resolvedBody: string
    /** Which channel the outreach went out on; decides the logged interaction type. */
    channel?: 'linkedin' | 'email' | 'sms'
  }): Promise<OutreachLogEntry> => {
    return apiCall('log-outreach', { method: 'POST', body: { action: 'send', ...data } })
  },

  list: async (params: { contactId?: string; clientId?: string }): Promise<OutreachLogEntry[]> => {
    const qs = new URLSearchParams()
    if (params.contactId) qs.set('contactId', params.contactId)
    if (params.clientId) qs.set('clientId', params.clientId)
    return apiCall<OutreachLogEntry[]>(`list-outreach-log?${qs}`)
  },

  /**
   * Reverses a just-logged outreach: soft-deletes the log entry and removes the
   * contact interaction it created. Backs the Undo on the Copy & Log toast.
   */
  undo: async (id: string): Promise<{ id: string }> => {
    return apiCall('log-outreach', { method: 'POST', body: { action: 'undo', id } })
  },

  updateFollowUp: async (id: string, followUpStatus: 'completed' | 'dismissed'): Promise<OutreachLogEntry> => {
    return apiCall('log-outreach', { method: 'POST', body: { action: 'follow-up', id, followUpStatus } })
  },
}

// ──────────────────────────────────────
// Executive Search Recruiters (MWA-253)
// Per-client list of executive-search / reverse-recruiting recruiters with a
// copyable outreach email template and a "Contacted" indicator.
// ──────────────────────────────────────

// MWA-357 — Search Firm and Wild Card Outreach share this record. Absent is
// search_firm: every row written before Wild Card existed is one.
export type OutreachType = 'search_firm' | 'wildcard'

export interface Recruiter {
  id: string
  clientId: string
  outreachType?: OutreachType
  name: string
  firm: string
  field: string // specialty / industry vertical
  email: string
  linkedinUrl: string
  emailTemplate: string
  contacted: boolean
  contactedAt?: string | null
  contactedBy?: string | null
  // MWA-310 — when to reach out again, defaulted to three months after first
  // contact. A displayed date, not a reminder that fires.
  reTouchAt?: string | null
  // MWA-356 — when the second touch actually went out, as opposed to
  // reTouchAt, which is when one is planned.
  followedUpAt?: string | null
  followedUpBy?: string | null
  outcomes?: RecruiterOutcome[]
  isDeleted?: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
}

// MWA-356 — what the partner said back, and when.
export interface RecruiterOutcome {
  id: string
  text: string
  occurredAt: string
  createdBy: string
  createdAt: string
  updatedAt?: string
  updatedBy?: string
}

export const recruitersApi = {
  list: async (params: { clientId?: string; outreachType?: OutreachType }): Promise<Recruiter[]> => {
    const qs = new URLSearchParams()
    if (params.clientId) qs.set('clientId', params.clientId)
    if (params.outreachType) qs.set('outreachType', params.outreachType)
    const query = qs.toString()
    return apiCall<Recruiter[]>(`list-recruiters${query ? `?${query}` : ''}`)
  },

  create: async (data: Partial<Recruiter> & { clientId: string; name: string }): Promise<Recruiter> => {
    return apiCall<Recruiter>('manage-recruiters', { method: 'POST', body: { action: 'create', ...data } })
  },

  update: async (data: Partial<Recruiter> & { id: string }): Promise<Recruiter> => {
    return apiCall<Recruiter>('manage-recruiters', { method: 'POST', body: { action: 'update', ...data } })
  },

  delete: async (id: string): Promise<{ id: string }> => {
    return apiCall<{ id: string }>('manage-recruiters', { method: 'POST', body: { action: 'delete', id } })
  },

  // Convenience wrapper around update for the "Mark Contacted" toggle.
  markContacted: async (id: string, contacted: boolean): Promise<Recruiter> => {
    return apiCall<Recruiter>('manage-recruiters', { method: 'POST', body: { action: 'update', id, contacted } })
  },

  markFollowedUp: async (id: string, followedUp: boolean): Promise<Recruiter> => {
    return apiCall<Recruiter>('manage-recruiters', { method: 'POST', body: { action: 'update', id, followedUp } })
  },

  addOutcome: async (id: string, outcome: { text: string; occurredAt?: string }): Promise<Recruiter> => {
    return apiCall<Recruiter>('manage-recruiters', { method: 'POST', body: { action: 'add-outcome', id, ...outcome } })
  },

  updateOutcome: async (
    id: string,
    outcomeId: string,
    changes: { text?: string; occurredAt?: string }
  ): Promise<Recruiter> => {
    return apiCall<Recruiter>('manage-recruiters', {
      method: 'POST',
      body: { action: 'update-outcome', id, outcomeId, ...changes },
    })
  },

  deleteOutcome: async (id: string, outcomeId: string): Promise<Recruiter> => {
    return apiCall<Recruiter>('manage-recruiters', {
      method: 'POST',
      body: { action: 'delete-outcome', id, outcomeId },
    })
  },
}

// ──────────────────────────────────────
// Target Companies (MWA-254 / MWA-239)
// A company's employer-quality rating and its "why it's on the list" sourcing
// category, kept here because the Target Companies tab still uses both.
//
// The standalone entity that used to own them - TargetCompanyRecord and
// targetCompaniesApi - is gone from the frontend. The list is read and written
// through clientsApi.get / clientsApi.saveDreamJob, on the client's dream job,
// which is the single place a company's tier is set.
//
// Its lambdas and its table are deliberately still in place: production has
// not been folded over yet, and removing the table from the production config
// would destroy it on the next apply.
// ──────────────────────────────────────

export type EmployerQuality = 'good' | 'fair' | 'poor'
export type TargetCompanyCategory = 'north_star' | 'competitor' | 'wildcard'

// ──────────────────────────────────────
// Client Health Dashboard
// ──────────────────────────────────────

export interface ClientHealthSummary {
  total: number
  hitGoal: number
  belowGoal: number
  zeroActivity: number
  coachUnassigned?: number
  weeklyGoal: number
  idleThreshold: number
  // Book-wide outcome metrics (surfaced alongside the engagement scorecard).
  interviewsHeld14d?: number // held interview events in the trailing 14 days
  interviewsHeldPrev14d?: number // held interview events in the 14 days before that (for trend)
  landingsQtd?: number // accepted offers in the current calendar quarter
  activeCount?: number // clients with status 'active' (book size for the capacity gauge)
  capacity?: number // configured active-client capacity ceiling (default 15)
  offersLive?: number // open job offers across the book (accepted offers excluded)
}

export interface ClientHealthEntry {
  clientId: string
  clientName: string
  clientEmail: string
  // Client Health lists every client at any stage, while the Job Search routes
  // only serve market-ready ones — so the page needs the stage to decide where
  // its per-client link can point.
  workflowStage: string | null
  touchpoints: number
  goal: number
  status: 'hit_goal' | 'below_goal' | 'zero_activity'
  daysSinceLastActivity: number | null
  staleContacts: number
  pendingCoachReviews?: number
  pendingMockFeedback?: number
  offersLive?: number // open job offers for this client (accepted offers excluded)
  assignedTo: string | null
  assignedToName?: string | null
}

export interface ClientHealthData {
  summary: ClientHealthSummary
  clients: ClientHealthEntry[]
}

export const clientHealthApi = {
  get: async (): Promise<ClientHealthData> => {
    return apiCall<ClientHealthData>('get-client-health')
  },
}
